import { useMemo } from 'react';
import type { StayZoneAreaData, StayZonePhoto } from '@shared/components/StayZoneTile/StayZoneTile.types';
import type { Maybe, TravelDistancesBetweenPointsResponseFragment } from '@graphql/generated';
import { useStayZonesQuery } from '@graphql/generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useStayZoneBestForTags } from './useStayZoneBestForTags';
import { addError } from '@shared/utils/logger';
import { DEFAULT_HOTEL_IMAGE } from '../constants';

interface UseStayZonesReturn
  extends Readonly<{
    loading: boolean;
    error?: Error;
    stayZones: ReadonlyArray<StayZoneAreaData>;
  }> {}

/**
 * Hook to get the stay zones data from GraphQL API.
 */
export const useStayZones = (): UseStayZonesReturn => {
  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;

  const { data, loading, error } = useStayZonesQuery({
    variables: { eventId: eventId || '' },
    skip: !eventId,
    batchMode: 'fast',
  });

  const { parseBestForTag } = useStayZoneBestForTags();

  // Source of truth for stay zones data with parsed fields from backend API
  const stayZones = useMemo(() => {
    // Return empty array if there's an error
    if (error) {
      addError(error, {
        context: 'useStayZones',
        message: 'Failed to fetch stay zones data',
        eventId,
      });
      return [];
    }

    const zones = data?.stayZones ?? [];
    return zones.map(stayZone => {
      const carouselPhotos = parseStayZonePhotos(stayZone.name, stayZone.photos);
      return {
        // Keep all fields from backend API
        ...stayZone,

        // Transform photos array to PhotoCarousel format
        carouselPhotos,

        // Transform distances array to the expected TravelDistancesBetweenPointsResponseFragment structure
        travelDistances: stayZone.distances ? ({ commonDistance: stayZone.distances } as TravelDistancesBetweenPointsResponseFragment) : undefined,

        // Fields parsed from backend API with different structure
        features: parseStayZoneFeatures(stayZone.whyThisZoneIsAGreatPick),
        bestForLabel: parseBestForTag(stayZone.bestForTags ?? []),
      };
    });
  }, [data?.stayZones, error, parseBestForTag, eventId]);

  return {
    loading,
    error,
    stayZones,
  };
};

const parseStayZoneFeatures = (whyThisZoneIsAGreatPick?: string | null): ReadonlyArray<string> => {
  return (
    whyThisZoneIsAGreatPick
      ?.split('\n')
      .map(line => line.replace(/^[•\-*→]\s*/, '').trim())
      .filter(Boolean) ?? []
  );
};

const parseStayZonePhotos = (name: string, photos?: Maybe<ReadonlyArray<{ url: string }>>): ReadonlyArray<StayZonePhoto> => {
  if (!photos || photos.length === 0) {
    return [{ src: DEFAULT_HOTEL_IMAGE, alt: name }];
  }
  return photos.map(photo => ({ src: photo.url, alt: name }));
};
